import { BaseService } from './base.service';
import { CustomResponseEntity, PaginationParams, PaginatedResponse } from '../types/common';
import { 
  TrainingProgressDto, 
  CreateTrainingProgressDto, 
  UpdateTrainingProgressDto,
  TrainingProgressSummaryDto,
  BatchProgressUpdateDto,
  TrainingEnrollmentProgressDto,
  BatchProgressByGroupDto,
  TrainingProgressPaginationParams
} from '../types/dtos/training';
import { api } from '../client/axiosClient';

/**
 * TrainingProgressService handles CRUD operations for training progress.
 *
 * URL Pattern: /api/v1/training/enrollments/{enrollmentId}/progress
 *               /api/v1/trainings/{trainingId}/progress
 *               /api/v1/contents/{contentId}/progress
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * VAL-FE-006: Training Service Works with Header-Based Auth
 * The axiosClient automatically includes X-Organization-Id header from localStorage,
 * which the backend uses for tenant context validation via TenantContextHolder.
 *
 * VAL-TRN-007: Training Progress with X-Organization-Id Header
 *
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param context - Object containing optional enrollmentId, trainingId, or contentId
 */
export class TrainingProgressService extends BaseService<
  TrainingProgressDto,
  CreateTrainingProgressDto,
  UpdateTrainingProgressDto
> {
  private enrollmentId?: string;
  private trainingId?: string;
  private contentId?: string;

  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param context - Object containing optional enrollmentId, trainingId, or contentId
   */
  constructor(
    _organizationId: string, 
    context: { enrollmentId?: string; trainingId?: string; contentId?: string }
  ) {
    // Base URL depends on context
    let baseUrl: string;
    
    if (context.enrollmentId) {
      baseUrl = `/api/v1/training/enrollments/${context.enrollmentId}/progress`;
    } else if (context.contentId) {
      baseUrl = `/api/v1/contents/${context.contentId}/progress`;
    } else if (context.trainingId) {
      baseUrl = `/api/v1/trainings/${context.trainingId}/progress`;
    } else {
      baseUrl = `/api/v1/training/progress`;
    }

    super(baseUrl);
    this.enrollmentId = context.enrollmentId;
    this.trainingId = context.trainingId;
    this.contentId = context.contentId;
  }

  findAll(params?: PaginationParams): Promise<CustomResponseEntity<TrainingProgressDto[]>> {
    return api.get(this.baseUrl, { params });
  }

  // Get progress summary for a training (includes summary + all progress details)
  getProgressSummary(): Promise<CustomResponseEntity<TrainingProgressSummaryDto>> {
    if (!this.trainingId) {
      throw new Error('Training ID is required for progress summary');
    }
    return api.get(`/api/v1/trainings/${this.trainingId}/progress`);
  }

  // Batch update progress for multiple organizations on a specific content
  batchUpdateProgress(
    trainingId: string, 
    contentId: string, 
    data: BatchProgressUpdateDto
  ): Promise<CustomResponseEntity<TrainingProgressDto[]>> {
    return api.post(
      `/api/v1/trainings/${trainingId}/contents/${contentId}/progress/batch`,
      data
    );
  }

  // Get paginated enrollment progress for a training
  getEnrollmentProgress(
    trainingId: string,
    params?: TrainingProgressPaginationParams
  ): Promise<CustomResponseEntity<PaginatedResponse<TrainingEnrollmentProgressDto>>> {
    return api.get(
      `/api/v1/trainings/${trainingId}/enrollments/progress`,
      { params }
    );
  }

  // Batch update progress by group
  batchUpdateByGroup(
    groupId: string,
    data: BatchProgressByGroupDto
  ): Promise<CustomResponseEntity<TrainingProgressDto[]>> {
    return api.post(
      `/api/v1/progress/batch/group/${groupId}`,
      data
    );
  }
}

/**
 * Factory function to create a TrainingProgressService instance
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param context - Object containing optional enrollmentId, trainingId, or contentId
 */
export const createTrainingProgressService = (
  _organizationId: string,
  context: { enrollmentId?: string; trainingId?: string; contentId?: string } = {}
) => new TrainingProgressService(_organizationId, context);

